import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isAxiosError } from "axios";
import { APP_ROUTES } from "../../constants";
import { getEvents, safeCapture } from "../../analytics";
import { decideSmsConsent } from "../../services/api";
import { useSmsConsentStore } from "../../store/useSmsConsentStore";
import { SmsConsentPopup } from "./SmsConsentPopup";
import { requestSmsConsent } from "./smsConsent";

/** A gate waiter continues the operation. Preferences reopens the sheet. */
function release(onDone: () => void) {
  const source = useSmsConsentStore.getState().finishWait();
  if (source !== "gate") onDone();
}

function errorCode(err: unknown): string | undefined {
  if (!isAxiosError(err)) return undefined;
  return (err.response?.data as { error?: { code?: string } } | undefined)
    ?.error?.code;
}

/**
 * Shows the pending SMS consent popup and records the answer. `onDone`
 * reopens the preferences sheet so the switch shows the saved state.
 */
export function SmsConsentController({
  linkId,
  onDone,
}: {
  linkId: string | null;
  onDone: () => void;
}) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const onPolicyPage = pathname === APP_ROUTES.SMS_TERMS_PAGE;
  const pending = useSmsConsentStore((s) => s.pending);
  const [busy, setBusy] = useState(false);
  const version = pending?.version;

  useEffect(() => {
    if (version && !onPolicyPage) {
      safeCapture(getEvents().SMS_CONSENT_VIEW, { version });
    }
  }, [version, onPolicyPage]);

  const decide = useCallback(
    async (decision: "accepted" | "declined") => {
      if (!linkId || !pending?.version || busy) return;
      setBusy(true);
      const language = i18n.language?.slice(0, 2) || "en";
      try {
        await decideSmsConsent(linkId, {
          decision,
          version: pending.version,
          language,
        });
        safeCapture(getEvents().SMS_CONSENT_DECIDED, {
          decision,
          version: pending.version,
          success: true,
        });
        release(onDone);
      } catch (err) {
        const code = errorCode(err);
        safeCapture(getEvents().SMS_CONSENT_DECIDED, {
          decision,
          version: pending.version,
          success: false,
          error_code: code,
        });
        if (code === "SMS_TERMS_VERSION_CHANGED") {
          const reloaded = await requestSmsConsent(linkId, language);
          if (reloaded) return;
        }
        release(onDone);
      } finally {
        setBusy(false);
      }
    },
    [busy, i18n.language, linkId, onDone, pending?.version],
  );

  // The policy page sits on top of the question; back brings the popup back.
  if (!pending || onPolicyPage) return null;

  return (
    <SmsConsentPopup
      consent={pending}
      busy={busy}
      onAccept={() => void decide("accepted")}
      onDecline={() => void decide("declined")}
      onViewPolicy={() => {
        safeCapture(getEvents().SMS_CONSENT_POLICY_CLICK, {
          version: pending.version,
        });
        navigate(APP_ROUTES.SMS_TERMS_PAGE);
      }}
    />
  );
}
